/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B100090 銀行マスタメンテ
 */
import { B100090Create, B100090Update, catchRestError } from "clients/rest";
import {
  msgState,
  SCodeSelect,
  SConfirmDialog,
  SFileButton,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  SText,
  STextField,
} from "components";
import { getClientMessage } from "messages";
import { B100090Mode, getSCode, SakujoyoteiKbn, SCode } from "messages/codes";
import { labels } from "messages/labels";
import { B000180, B100910 } from "pages";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { mode } from "services/codes";
import { focusState, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  modeKbn: SCode;
  ginkoCd: string;
  ginkoshitenCd: string;
  ginkoNm: string;
  shitenNm: string;
  sakujoyoteiKbn: SCode;
  ginkoKana: string;
  shitenKana: string;
  version: number;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  modeKbn: getSCode(B100090Mode, "0"),
  ginkoCd: "",
  ginkoshitenCd: "",
  ginkoNm: "",
  shitenNm: "",
  sakujoyoteiKbn: getSCode(SakujoyoteiKbn, "0"),
  ginkoKana: "",
  shitenKana: "",
  version: undefined,
};

const focusId = "B100090";
// 画面ID
const gamenId = "100090";

// B100090 銀行マスタメンテ
export const B100090 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });

  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();
  // 更新確認ダイアログオープン用
  const [openUpdateConfirm, setOpenUpdateConfirm] = useState(false);
  // 活性設定
  // 新規モードかどうか
  const isShinki = methods.watch("modeKbn")?.value === mode.shinki;
  // 修正モードかどうか
  const isShusei = methods.watch("modeKbn")?.value === mode.shusei;

  // 初期表示イベント
  useEffect(() => {
    focus(1);
  }, []);

  const handleClickUpdateButton = () => {
    // 新規の場合
    if (isShinki) {
      B100090Create(methods)
        .then(() => {
          methods.reset();
          methods.reset({
            ...methods.getValues(),
            ginkoCd: "",
          });
          setMsg(getClientMessage("B100090_001"));
        })
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
        });
    }
    // 更新の場合
    else {
      B100090Update(methods)
        .then(() => {
          methods.reset();
          methods.reset({
            ...methods.getValues(),
            ginkoCd: "",
          });
          setMsg(getClientMessage("B100090_002"));
        })
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
        });
    }
  };

  // レイアウト設定
  return (
    <>
      <FormProvider {...methods}>
        <SFrame
          title="銀行マスタメンテ"
          gridTemplateColumns="7rem 6rem 9rem 4rem 2rem 6rem 19rem "
        >
          <SText gridColumn="span 6"></SText>
          <SFileButton
            label={labels.buttons.excel}
            align="right"
            {...register(10)}
            dialog={<B100910 gamenId={gamenId} />}
          />
          <SLabel required gridColumn="1">
            モード
          </SLabel>
          <SCodeSelect
            required
            name="modeKbn"
            label="モード"
            options={B100090Mode}
            {...register(1)}
          />

          <SLabel required gridColumn="1">
            銀行コード
          </SLabel>

          {isShinki ? (
            <>
              <SNumberField
                name="ginkoCd"
                label="銀行コード"
                code
                required
                size={4}
                {...register(2)}
              />
            </>
          ) : (
            <>
              <SNumberField
                name="ginkoCd"
                label="銀行コード"
                code
                required
                size={4}
                {...register(2)}

                // ↓API実装完了後、更新する
                // onBlur={() => B000180GinkoNm(meiMethods, navigate, setMsg)}
              />
            </>
          )}

          {isShinki ? (
            <>
              <SNumberField
                name="ginkoshitenCd"
                label="銀行支店コード"
                code
                required
                size={3}
                {...register(3)}
              />
            </>
          ) : (
            <>
              <SNumberField
                name="ginkoshitenCd"
                label="銀行支店コード"
                code
                required
                size={3}
                {...register(3)}
                // ↓API実装完了後、更新する（削除予定区分を無視するように変える）
                dialog={<B000180 />}
                handleSubmit={(data: any) => {
                  methods.setValue("ginkoCd", data.ginkoCd);
                  methods.setValue("ginkoshitenCd", data.ginkoshitenCd);
                  methods.setValue("shitenNm", data.shitenNm);
                  methods.setValue("shitenKana", data.shitenKana);
                  methods.setValue("sakujoyoteiKbn", data.sakujoyoteiKbn);
                }}
                // onBlur={() => B000180GinkoNm(meiMethods, navigate, setMsg)}
              />
            </>
          )}

          <SLabel required gridColumn="1">
            銀行名
          </SLabel>
          <STextField
            name="ginkoNm"
            label="銀行名"
            code
            required
            size={20}
            {...register(4)}
            gridColumn="span 3"
          />

          <SLabel gridColumn="6">銀行カナ名</SLabel>
          <STextField
            name="ginkoNmKana"
            label="銀行カナ名"
            size={20}
            {...register(5)}
          />

          <SLabel required gridColumn="1">
            支店名
          </SLabel>
          <STextField
            name="shitenNm"
            label="支店名"
            code
            required
            size={20}
            {...register(6)}
            gridColumn="span 3"
          />

          <SLabel gridColumn="6">支店カナ名</SLabel>
          <STextField
            name="shitenNmKana"
            label="支店カナ名"
            size={20}
            {...register(7)}
          />

          <SLabel gridColumn="1">削除予定区分</SLabel>
          <SCodeSelect
            name="sakujoyoteiKbn"
            options={SakujoyoteiKbn}
            {...register(8)}
          />

          <SNormalButton
            label={labels.buttons.update}
            align="right"
            {...register(9)}
            gridColumn="7"
            gridRow="8"
            dialog={
              <SConfirmDialog
                title="マスタ更新"
                msg="更新します。よろしいですか？"
              />
            }
            dialogAction="confirm"
            handleSubmit={handleClickUpdateButton}
          />
        </SFrame>
      </FormProvider>
    </>
  );
};
